import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomerStatusTableDataAsync } from "../../redux/reducers/customerStatusTable";
import PaginationComponent from "../../components/pagination/pagination";
import "./customerStatusTable.css";
import ApiLoader from "../../components/apiLoader/apiLoader";
import StatusSelector from "../../components/statusSelector/statusSelector";
import DateRangePickerComp from "../../components/dateRangePicker/dateRangePicker";
import {
  clearCreditCardDetails,
  clearCustomerData,
  clearLimit,
} from "../../redux/reducers/customerAgentDetailsDashboard";
import { ReactComponent as NoDataTile } from "../../assets/icons/no-data.svg";
import { ReactComponent as Arrow } from "../../assets/icons/sort.svg";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { format } from "date-fns";
import { fetchStatusesAsync } from "../../redux/reducers/orderStatus";
import { downloadCsvAsync } from "../../redux/reducers/downloadCsvSlice";
const StatusChip = React.memo(({ status }) => {
  const statusConfig = useMemo(
    () => ({
      completed: { class: "completed", color: "#2DB683", text: "Completed" },
      rejected_ineligible: {
        class: "rejected",
        color: "#FF6347",
        text: "Rejected Ineligible",
      },
      rejected_invalid: {
        class: "rejected",
        color: "#FF6347",
        text: "Rejected Invalid",
      },
      rejected_ineligible_porting: {
        class: "rejected_ineligible",
        color: "#FF6347",
        text: "Rejected Ineligible for Porting",
      },
      rejected_device_not_supported: {
        class: "rejected_ineligible",
        color: "#FF6347",
        text: "Rejected Device Not Supported",
      },
      rejected_other: {
        class: "rejected",
        color: "#FF6347",
        text: "Rejected Other",
      },
      reinitiated: {
        class: "reinitiated",
        color: "#A18ADC",
        text: "Reinitiated",
      },
      open: { class: "open", color: "#28A5DD", text: "Open" },
      pending: { class: "pending", color: "#FDAB3D", text: "Pending" },
      card_removed: {
        class: "card-removed",
        color: "#909090",
        text: "Card Removed",
      },
    }),
    [status]
  );

  const {
    class: chipClass,
    color: chipColor,
    text: displayText,
  } = statusConfig[status?.toLowerCase()] || {};

  return (
    <span
      className={`status-chip ${chipClass}`}
      style={{ backgroundColor: chipColor }}
    >
      {displayText || "Unknown"}
    </span>
  );
});

const TableHeader = React.memo(({ isAscending, toggleSortDirection }) => {
  const headers = [
    "Business name",
    "Customer name",
    "Phone number",
    "Zip code",
    "Email address",
    "Created on",
    "Status",
  ];

  return (
    <TableHead>
      <TableRow>
        {headers.map((header, index) => (
          <TableCell
            key={header}
            className={`status-table-header-cell ${
              index === 0 ? "status-table-header" : ""
            }${index === 5 ? "status-table-created-on" : ""}`}
          >
            {header}
            {header === "Created on" && (
              <Arrow
                className={`sort-arrow ${
                  isAscending ? "ascending" : "descending"
                }`}
                onClick={toggleSortDirection}
              />
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
});

const TableRowData = React.memo(({ row, id, filterData }) => {
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    sessionStorage.setItem("filterData", JSON.stringify(filterData));
    navigate(`/customer-service-dashboard/${id}`, {
      state: { row },
    });
  }, [filterData, id, navigate, row]);

  return (
    <TableRow className="status-table-row" onClick={handleClick}>
      <TableCell className="status-table-row-business">
        {row.business}
      </TableCell>
      <TableCell className="status-table-row-customer">
        {row.customer}
      </TableCell>
      <TableCell>{row.phone}</TableCell>
      <TableCell>{row.zip}</TableCell>
      <TableCell>{row.email}</TableCell>
      <TableCell>{format(new Date(row.createdAt), "MM-dd-yyyy")}</TableCell>
      <TableCell sx={{ width: "106px", paddingRight: "30px" }}>
        <StatusChip status={row.status} />
      </TableCell>
    </TableRow>
  );
});

const CustomerStatusTable = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const urlParams = new URLSearchParams(useLocation().search);
  const [statusId, setStatusId] = useState(
    urlParams.get("status") || "PENDING"
  );
  const [dateRange, setDateRange] = useState([
    urlParams.get("fromDate") ? new Date(urlParams.get("fromDate")) : null,
    urlParams.get("toDate") ? new Date(urlParams.get("toDate")) : null,
  ]);

  const [isDataAvailable, setIsDataAvailable] = useState();
  const [isAscending, setIsAscending] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const customerStatusTableData = useSelector(
    (state) => state?.statusTable?.customerStatusTableData
  );
  const isLoading = useSelector((state) => state?.statusTable?.isLoading);

  const toggleSortDirection = () => {
    setIsAscending((prevState) => !prevState);
  };

  const formattedData = useMemo(
    () =>
      customerStatusTableData?.orders?.map((item) => ({
        id: item.id,
        business: item.customerData.business_name,
        customer: item.customerData.customer_name,
        phone: item.customerData.phone_number,
        zip: item.customerData.zipcode,
        email: item.customerData.email,
        status: item.status,
        billingAddress: item.customerData.billing_address,
        taxId: item.customerData.tax_id,
        accountNumber: item.customerData.account_number,
        createdAt: item.customerData.created_at,
      })) || [],
    [customerStatusTableData]
  );

  const totalPages = useMemo(
    () =>
      Math.ceil(
        customerStatusTableData?.totalCount
          ? customerStatusTableData.totalCount / rowsPerPage
          : 1
      ),
    [customerStatusTableData?.totalCount, rowsPerPage]
  );

  useEffect(() => {
    setPage(1);
  }, [statusId]);

  const updateURLAndFetchData = useCallback(() => {
    const [startDate, endDate] = dateRange || [];

    const payload = {
      ...(statusId !== "ALL" ? { status: statusId } : {}),
      ...(startDate
        ? { fromDate: format(new Date(startDate), "yyyy-MM-dd") }
        : {}),
      ...(endDate ? { toDate: format(new Date(endDate), "yyyy-MM-dd") } : {}),
      page,
      limit: rowsPerPage,
      sort_by: isAscending ? "ASC" : "DESC",
    };

    const params = new URLSearchParams(
      Object.entries(payload).reduce((acc, [key, value]) => {
        if (value) acc[key] = value;
        return acc;
      }, {})
    );

    if (statusId) {
      params.set("status", statusId);
    }

    navigate(`/customer-status?${params.toString()}`);
    dispatch(fetchCustomerStatusTableDataAsync(payload)).then((response) => {
      setIsDataAvailable(!response.error);
    });
  }, [dateRange, dispatch, navigate, page, rowsPerPage, statusId, isAscending]);

  const handleExport = (dateRange, isAscending, status) => {
    const payload = {
      sort_by: isAscending ? "ASC" : "DESC",
      status,
    };

    if (dateRange[0]) {
      payload.from_date = format(new Date(dateRange[0]), "yyyy-MM-dd");
    }
    if (dateRange[1]) {
      payload.to_date = format(new Date(dateRange[1]), "yyyy-MM-dd");
    }

    // Remove undefined values from the payload
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    dispatch(downloadCsvAsync(payload));
  };

  useEffect(() => {
    dispatch(clearLimit());
    dispatch(clearCreditCardDetails());
    dispatch(fetchStatusesAsync());
    dispatch(clearCustomerData());
    window.addEventListener("popstate", () =>
      localStorage.removeItem("myLocalStorageKey")
    );
    return () => {
      window.removeEventListener("popstate", () =>
        localStorage.removeItem("myLocalStorageKey")
      );
    };
  }, [dispatch]);

  useEffect(() => {
    updateURLAndFetchData();
  }, [updateURLAndFetchData]);

  useEffect(() => {
    if (localStorage.getItem("customerInfo")) {
      localStorage.removeItem("customerInfo");
    }
  }, []);

  return (
    <div className="status-table">
      <ApiLoader loading={isLoading} />
      <div className="header-section">
        <div className="table-heading">Order Listing</div>
        <div className="filter-container">
          <button
            className="exoprt-btn"
            onClick={() => handleExport(dateRange, isAscending, statusId)}
          >
            <Export />
            Export CSV
          </button>
          <p className="date-title">Date</p>
          <DateRangePickerComp
            dateRange={dateRange}
            setDateRange={setDateRange}
          />
          <StatusSelector setStatusId={setStatusId} />
        </div>
      </div>
      <Paper className="status-table-paper">
        <TableContainer className={`${isDataAvailable ? "" : "no-data-table"}`}>
          <Table>
            <TableHeader
              isAscending={isAscending}
              toggleSortDirection={toggleSortDirection}
            />
            <TableBody>
              {formattedData?.length > 0 ? (
                formattedData?.map((row, index) => (
                  <TableRowData
                    key={`${row.customer}-${index}`}
                    row={row}
                    id={row.id}
                    filterData={{ statusId, ...dateRange }}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell className="no-data-col" colSpan={6}>
                    <div className="no-data-state">
                      <NoDataTile />
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <div className="pagination-container">
        <PaginationComponent
          totalPages={totalPages}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
        />
      </div>
    </div>
  );
};

export default CustomerStatusTable;
